import { useQueryClient } from '@tanstack/react-query'
import { ChevronDownIcon } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { useCurrentUser } from '@/api/auth/queries/useCurrentUser'
import { usePickupOrders } from '@/api/pickup-orders/queries/usePickupOrders'
import { PickupOrderStatus } from '@/api/pickup-orders/types/PickupOrder'
import { ServiceReqStatusEnum, useServiceReqs } from '@/api/service-req'
import { useUSAPkgLookups } from '@/api/usa-pkg-lookup/queries/useUSAPkgLookups'
import { PkgWarehouse } from '@/api/user-package/types/UserPkg'
import { Badge } from '@/components/ui/badge'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { useIsMobile } from '@/hooks/useIsMobile'
import { useOfficePagePermissions } from '@/hooks/useOfficePagePermissions'
import { clearImpersonation } from '@/lib/impersonation'
import { QUERY_KEYS } from '@/lib/queryKeys'
import { cn } from '@/lib/utils'
import { OfficeRoutePath } from '@/RoutePath'

import { usePkgReqCounts } from '../../api/package-req/queries/usePkgReqCounts'
import grFlagCircle from '../../imgs/flags/greece-circle.png'
import uaeFlagCircle from '../../imgs/flags/uae-circle.png'
import ukFlagCircle from '../../imgs/flags/uk-circle.png'
import usaFlagCircle from '../../imgs/flags/usa-circle.png'
import logo from '../../imgs/logo.png'
import logoutIcon from '../../imgs/logout_icon.svg'
import { setIsAuthenticated } from '../../store/appSlice'

type NavItemChild = {
  label: string | React.ReactNode
  to: string
}

type NavItem = {
  label: string | React.ReactNode
  badge?: number
  to?: string
  children?: NavItemChild[]
}

export default function OfficeNavbar() {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const isMobile = useIsMobile()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const { i18n } = useTranslation()
  const { data: loggedUser } = useCurrentUser()
  const queryClient = useQueryClient()
  const { data: pkgReqCounts } = usePkgReqCounts()
  const { data: serviceReqs } = useServiceReqs()

  const isUserRoutes = !location.pathname.includes('/office')
  // Calculate total sum (excludes count/measure, matching the historical badge)
  const allReqsSum =
    (pkgReqCounts?.shrinkRequest ?? 0) +
    (pkgReqCounts?.photoRequest ?? 0) +
    (pkgReqCounts?.returnRequest ?? 0) +
    (pkgReqCounts?.uniteRequest ?? 0) +
    (pkgReqCounts?.printRequest ?? 0) +
    (pkgReqCounts?.newXltRequest ?? 0)
  const pendingServiceReqsSum = serviceReqs?.filter(req => req.currentStatus === ServiceReqStatusEnum.Pending).length ?? 0

  const logOutHandler = async () => {
    clearImpersonation()
    localStorage.removeItem('cognito-tokens')
    dispatch(setIsAuthenticated(false))
    queryClient.setQueryData([QUERY_KEYS.LOGGED_USER], null)
    queryClient.clear()
    navigate('/')
  }

  function toggleMenu() {
    if (!isMobile) return
    setIsMenuOpen(prev => !prev)
  }

  // eslint-disable-next-line unused-imports/no-unused-vars
  const toggleLanguage = () => {
    const nextLang = i18n.language === 'he' ? 'en' : 'he'
    i18n.changeLanguage(nextLang)
    setIsMenuOpen(false)
  }

  const isRouteActive = (route: string) => {
    const pathname = location.pathname

    if (route === OfficeRoutePath.AddNewPkg) {
      return pathname === route
    }

    return pathname === route || pathname.startsWith(`${route}/`)
  }

  const isItemActive = (item: NavItem) => {
    if (item.children) {
      return item.children.some(child => location.pathname === child.to)
    }
    return item.to ? isRouteActive(item.to) : false
  }

  const permissions = useOfficePagePermissions()

  const { data: pkgLookups } = useUSAPkgLookups(permissions.canViewPkgLookup || permissions.canViewPkgLookupsList)
  const { data: pickupOrders } = usePickupOrders(PickupOrderStatus.Pending, permissions.canViewPickupOrders)

  const pkgLookupsCount = pkgLookups?.length ?? 0
  const pickupOrdersCount = pickupOrders?.length ?? 0
  const items = useMemo<NavItem[]>(() => {
    if (!permissions.isStandardOfficeUser) {
      if (permissions.canViewScanPickupOrders) {
        return [{ label: 'Pickup Orders', to: OfficeRoutePath.ScanPickupOrders }]
      }
      if (permissions.canViewAffiliateManager) {
        return [{ label: 'Affiliate Manager', to: OfficeRoutePath.AffiliateManager }]
      }
      return []
    }

    return [
      ...(permissions.canViewAddNewPkg ? [{ label: 'Add New Package', to: OfficeRoutePath.AddNewPkg }] : []),
      ...(permissions.canViewPkgLocation ? [{ label: 'Location', to: OfficeRoutePath.PkgLocation }] : []),
      ...(permissions.canViewBarcodeGunShip ? [{ label: 'Barcode Gun Ship', to: OfficeRoutePath.BarcodeGunShip }] : []),
      ...(permissions.canViewShelfs ? [{ label: 'Browse Shelves', to: OfficeRoutePath.Shelfs }] : []),
      ...(permissions.canViewPackageRequests ? [{ label: 'Package Requests', badge: allReqsSum, to: OfficeRoutePath.PkgRequests }] : []),
      ...(permissions.canViewShip ? [{ label: 'Ship Packages', to: OfficeRoutePath.Ship }] : []),
      ...(permissions.canViewPkgChecklist ? [{ label: 'Packages Check List', to: OfficeRoutePath.CheckForm }] : []),
      ...(permissions.canViewWarehouseArrivals ? [{ label: 'Warehouse Arrivals', to: OfficeRoutePath.WarehouseArrivals }] : []),
      ...(permissions.canViewEditAndUpdate ? [{ label: 'Find & Update', to: OfficeRoutePath.EditAndUpdate }] : []),
      ...(permissions.canViewServiceReqs
        ? [{ label: 'Service Requests', badge: pendingServiceReqsSum, to: OfficeRoutePath.ServiceReqs }]
        : []),
      ...(permissions.canViewPkgLookup || permissions.canViewPkgLookupsList
        ? [
            {
              label: 'Package Lookup',
              badge: pkgLookupsCount,
              children: [
                ...(permissions.canViewPkgLookup ? [{ label: 'Lookup', to: OfficeRoutePath.PkgLookup }] : []),
                ...(permissions.canViewPkgLookupsList ? [{ label: 'Lookups List', to: OfficeRoutePath.PkgLookupsList }] : []),
              ],
            },
          ]
        : []),
      ...(permissions.canViewDelayedPkgs ? [{ label: 'Delayed Packages', to: OfficeRoutePath.DelayedPkgs }] : []),
      ...(permissions.canViewStats ? [{ label: 'Stats', to: OfficeRoutePath.Stats }] : []),
      ...(permissions.canViewPickupOrders
        ? [
            {
              label: 'Pickup Orders',
              badge: pickupOrdersCount,
              children: [
                { label: 'All Orders', to: OfficeRoutePath.PickupOrders },
                { label: 'Scan Orders', to: OfficeRoutePath.ScanPickupOrders },
              ],
            },
          ]
        : []),
      ...(permissions.canViewPopularBrandsItems ? [{ label: 'Popular Brands & Items', to: OfficeRoutePath.PopularBrandsItems }] : []),
      ...(permissions.canViewAdminPages
        ? [
            {
              label: 'Admin',
              children: [
                { label: 'Affiliate', to: OfficeRoutePath.Affiliate },
                { label: 'Coupons', to: OfficeRoutePath.Coupon },
                ...(permissions.isAdmin ? [{ label: 'Settings', to: OfficeRoutePath.AdminSettings }] : []),
                { label: 'Data', to: OfficeRoutePath.AdminData },
                ...(permissions.isAdmin ? [{ label: 'Audit Log', to: OfficeRoutePath.AuditLog }] : []),
                ...(permissions.isAdmin ? [{ label: 'Receipt Textract', to: OfficeRoutePath.ReceiptTextractTest }] : []),
                ...(permissions.canViewAffiliateManager ? [{ label: 'Affiliate Manager', to: OfficeRoutePath.AffiliateManager }] : []),
              ],
            },
          ]
        : []),
    ]
  }, [permissions, allReqsSum, pendingServiceReqsSum, pkgLookupsCount, pickupOrdersCount])

  const getFlag = () => {
    const warehouse = loggedUser?.warehouse
    if (warehouse === PkgWarehouse.USA) return usaFlagCircle
    if (warehouse === PkgWarehouse.GR) return grFlagCircle
    if (warehouse === PkgWarehouse.UK) return ukFlagCircle
    if (warehouse === PkgWarehouse.UAE) return uaeFlagCircle
    return null
  }

  const flagSrc = getFlag()

  return (
    <header
      dir='ltr'
      className={cn('no-print fixed start-0 top-0 z-50 w-full border-b border-gray-200 bg-white shadow-sm', isUserRoutes && 'static')}
    >
      <nav className='relative !flex h-[var(--navbar-height)] items-center justify-between px-4 md:px-6'>
        {/* Logo and Flag Section */}
        <div className='relative flex w-fit items-center gap-3 pe-8'>
          <Link to='/' className='flex items-center justify-center transition-opacity hover:opacity-80'>
            <img alt='logo' className='w-12 min-w-12' src={logo} />
          </Link>
          {flagSrc && (
            <div className='absolute end-4 bottom-0 flex items-center'>
              <img alt='flag' className='h-5 w-5' src={flagSrc} />
            </div>
          )}
        </div>

        {/* Desktop Navigation */}
        <ul className='m-0 hidden h-full list-none items-stretch justify-between overflow-x-auto p-0 md:flex'>
          {items.map((item, index) => (
            <li key={item.to || `item-${index}`} className='flex h-full items-center border-r border-gray-200 last:border-r-0'>
              {item.children ? (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    className={cn(
                      'flex h-full items-center gap-1 rounded-md px-3 text-center text-sm font-medium !text-gray-700 transition-all duration-200 outline-none',
                      'hover:bg-yellow-50 hover:text-yellow-500',
                      isItemActive(item) && '!bg-yellow-50 font-semibold !text-yellow-600'
                    )}
                  >
                    {item.label}
                    {!!item.badge && (
                      <Badge size='sm' variant='destructive' className='rounded-full'>
                        {item.badge}
                      </Badge>
                    )}
                    <ChevronDownIcon className='size-4' />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='start'>
                    {item.children.map(child => (
                      <DropdownMenuItem key={child.to} asChild dir='ltr'>
                        <Link
                          to={child.to}
                          className={cn(
                            'flex w-full cursor-pointer items-center p-3.5!',
                            location.pathname === child.to && 'bg-yellow-50 font-semibold text-yellow-600'
                          )}
                        >
                          {child.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  to={item.to!}
                  className={cn(
                    'flex h-full items-center rounded-md px-3 text-center text-sm font-medium !text-gray-700 transition-all duration-200',
                    'hover:bg-yellow-50 hover:text-yellow-500',
                    isRouteActive(item.to!) && '!bg-yellow-50 font-semibold !text-yellow-600'
                  )}
                >
                  {item.label}
                  {!!item.badge && (
                    <Badge size='sm' variant='destructive' className='ms-1 rounded-full'>
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              )}
            </li>
          ))}
        </ul>

        {/* Logout Button */}
        <div className='flex min-w-[50px] items-center justify-end gap-4'>
          {/* <button
            type='button'
            onClick={toggleLanguage}
            className='rounded-md p-1.5 text-lg transition-colors hover:bg-gray-100'
            title={i18n.language === 'he' ? 'English' : 'עברית'}
            aria-label={i18n.language === 'he' ? 'Switch to English' : 'החלף לעברית'}
          >
            {i18n.language === 'he' ? '🇺🇸' : '🇮🇱'}
          </button> */}
          <img alt='logout' className='size-6 cursor-pointer' onClick={logOutHandler} src={logoutIcon} />

          {/* Mobile Hamburger Menu */}
          <button
            onClick={toggleMenu}
            className={cn(
              'flex flex-col gap-1.5 rounded-sm border-0 !bg-gray-100 p-2 transition-all duration-300 md:hidden',
              isMenuOpen && 'rotate-90'
            )}
            aria-label='Toggle menu'
            aria-expanded={isMenuOpen}
          >
            <span className={cn('block h-0.5 w-6 bg-gray-800 transition-all duration-300', isMenuOpen && 'translate-y-2 rotate-45')} />
            <span className={cn('block h-0.5 w-6 bg-gray-800 transition-all duration-300', isMenuOpen && 'opacity-0')} />
            <span className={cn('block h-0.5 w-6 bg-gray-800 transition-all duration-300', isMenuOpen && '-translate-y-2 -rotate-45')} />
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={cn(
            'absolute start-0 top-14 z-[100] max-h-[calc(100dvh-56px)] w-full overflow-y-auto border-b border-gray-200 bg-white shadow-lg transition-all duration-300 ease-in-out md:hidden',
            isMenuOpen ? 'visible opacity-100' : 'pointer-events-none invisible opacity-0'
          )}
        >
          <ul className='flex list-none flex-col p-0 py-2'>
            {items.map((item, index) => (
              <li key={item.to || `item-${index}`}>
                {item.children ? (
                  <div className='px-6 py-3'>
                    <div
                      className={cn(
                        'mb-2 text-base font-medium !text-gray-700',
                        isItemActive(item) && 'rounded-md border-s-4 border-yellow-500 bg-yellow-50 px-3 py-2 font-semibold text-yellow-600'
                      )}
                    >
                      {item.label}
                    </div>
                    <div className='flex flex-col gap-1 ps-4'>
                      {item.children.map(child => (
                        <Link
                          key={child.to}
                          to={child.to}
                          onClick={() => setIsMenuOpen(false)}
                          className={cn(
                            'block rounded-md px-3 py-2 text-sm font-medium !text-gray-700 transition-colors duration-200',
                            'hover:bg-yellow-50 hover:text-yellow-600',
                            location.pathname === child.to && 'border-s-4 border-yellow-500 bg-yellow-50 font-semibold text-yellow-600'
                          )}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.to!}
                    onClick={() => setIsMenuOpen(false)}
                    className={cn(
                      'block px-6 py-3 text-base font-medium !text-gray-700 transition-colors duration-200',
                      'hover:bg-yellow-50 hover:text-yellow-600',
                      isRouteActive(item.to!) && 'border-s-4 border-yellow-500 bg-yellow-50 font-semibold text-yellow-600'
                    )}
                  >
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </header>
  )
}
